import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Plus, Users, MessageSquare, FileText, Eye, BarChart3 } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Dashboard = () => {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    fetchDashboardStats();
  }, []);

  const fetchDashboardStats = async () => {
    try {
      const response = await axios.get(`${API}/dashboard`);
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching dashboard stats:', error);
      toast.error('Failed to load dashboard statistics');
    }
  };

  if (!stats) {
    return (
      <div className="max-w-7xl mx-auto">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-64 mb-4"></div>
          <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-96 mb-8"></div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="h-32 bg-gray-200 dark:bg-gray-700 rounded-lg"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-slate-800 dark:text-slate-200">Dashboard</h1>
        <p className="text-slate-600 dark:text-slate-400 mt-2">Overview of your driving instruction activities</p>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
          <CardHeader className="pb-3">
            <CardTitle className="text-blue-800 flex items-center">
              <Users className="mr-2" size={20} />
              Total Pupils
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-blue-900">{stats.pupils_count}</div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-emerald-50 to-emerald-100 border-emerald-200">
          <CardHeader className="pb-3">
            <CardTitle className="text-emerald-800 flex items-center">
              <MessageSquare className="mr-2" size={20} />
              Active Questions
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-emerald-900">{stats.questions_count}</div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-amber-50 to-amber-100 border-amber-200">
          <CardHeader className="pb-3">
            <CardTitle className="text-amber-800 flex items-center">
              Total Logs
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-amber-900">{stats.logs_count}</div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
          <CardHeader className="pb-3">
            <CardTitle className="text-purple-800 flex items-center">
              <BarChart3 className="mr-2" size={20} />
              Total Hours
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-purple-900">{stats.total_hours}h</div>
          </CardContent>
        </Card>
      </div>

      <div className="mt-8">
        <Card>
          <CardHeader>
            <CardTitle>Quick Actions</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Link to="/pupils">
                <Button className="w-full h-16 text-lg">
                  <Plus className="mr-2" />
                  Add New Pupil
                </Button>
              </Link>
              <Link to="/questions">
                <Button className="w-full h-16 text-lg" variant="secondary">
                  <Plus className="mr-2" />
                  Add New Question
                </Button>
              </Link>
              <Link to="/logs">
                <Button className="w-full h-16 text-lg" variant="outline">
                  <FileText className="mr-2" />
                  Create Reflection Log
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Your Learners Section */}
      {stats.pupils_detailed && stats.pupils_detailed.length > 0 && (
        <div className="mt-8">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <Users className="mr-2" size={20} />
                Your Learners
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="divide-y divide-gray-200 dark:divide-gray-700">
                {stats.pupils_detailed.map((pupil) => (
                  <Link 
                    key={pupil.id} 
                    to={`/pupil/${pupil.id}/logs`}
                    className="flex items-center justify-between p-4 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                  >
                    <div className="flex items-center space-x-4">
                      {/* Avatar */}
                      <div className="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center text-white font-semibold">
                        {pupil.name.charAt(0).toUpperCase()}
                      </div>
                      
                      {/* Name and Stats */}
                      <div>
                        <h3 className="font-medium text-gray-900 dark:text-gray-100">{pupil.name}</h3>
                        <div className="flex items-center space-x-3 text-sm text-gray-500 dark:text-green-600">
                          <span>{pupil.total_hours}h</span>
                          <span>•</span>
                          <span>{pupil.lesson_count} lessons</span>
                          {pupil.last_lesson_date && (
                            <>
                              <span>•</span>
                              <span>Last: {new Date(pupil.last_lesson_date).toLocaleDateString()}</span>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    {/* Test Date Badge */}
                    <div className="flex items-center space-x-3">
                      {pupil.test_booked && pupil.test_date && (
                        <div className="flex flex-col items-end gap-1">
                          <Badge className="bg-blue-100 text-blue-800 border-blue-200">
                            Test: {new Date(pupil.test_date).toLocaleDateString()}
                          </Badge>
                          {pupil.last_cancel_date && (
                            <Badge className="bg-red-100 text-red-700 border-red-300 font-semibold">
                              Last Cancel Date: {new Date(pupil.last_cancel_date).toLocaleDateString()}
                            </Badge>
                          )}
                        </div>
                      )}
                      <Eye size={18} className="text-green-600" />
                    </div>
                  </Link>
                ))}
              </div>
              
              {stats.pupils_detailed.length === 0 && (
                <div className="text-center py-8 text-gray-500 dark:text-green-600">
                  <Users size={48} className="mx-auto mb-4 opacity-50" />
                  <p>No learners yet. Add your first pupil to get started!</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};


export default Dashboard;
